export const DEFAULT_CLERK_PASSWORD_MIN_LENGTH = 12;

export function normalizeClerkPasswordPolicy(settings = {}) {
  const minLength = Number(
    settings.min_length ??
      settings.minLength ??
      DEFAULT_CLERK_PASSWORD_MIN_LENGTH
  );
  const maxLength = Number(settings.max_length ?? settings.maxLength ?? 0);

  return {
    minLength:
      Number.isFinite(minLength) && minLength > 0
        ? minLength
        : DEFAULT_CLERK_PASSWORD_MIN_LENGTH,
    maxLength: Number.isFinite(maxLength) && maxLength > 0 ? maxLength : null,
    requireLowercase: Boolean(
      settings.require_lowercase ?? settings.requireLowercase
    ),
    requireUppercase: Boolean(
      settings.require_uppercase ?? settings.requireUppercase
    ),
    requireNumbers: Boolean(
      settings.require_numbers ?? settings.requireNumbers
    ),
    requireSpecialChar: Boolean(
      settings.require_special_char ?? settings.requireSpecialChar
    ),
    allowedSpecialCharacters:
      settings.allowed_special_characters ??
      settings.allowedSpecialCharacters ??
      "",
    requireStrength: Boolean(settings.show_zxcvbn ?? settings.showZxcvbn),
  };
}

export function getClerkPasswordPolicy(clerk) {
  const settings =
    clerk?.__internal_environment?.userSettings?.passwordSettings || {};
  return normalizeClerkPasswordPolicy(settings);
}

function containsAllowedSpecialCharacter(password, allowedCharacters) {
  if (!allowedCharacters) {
    return /[^A-Za-z0-9\s]/.test(password);
  }

  return [...allowedCharacters].some((character) =>
    password.includes(character)
  );
}

export function getPasswordPolicyChecks(password = "", policy) {
  const normalizedPolicy = normalizeClerkPasswordPolicy(policy);
  const checks = [
    {
      key: "minLength",
      label: `Almeno ${normalizedPolicy.minLength} caratteri`,
      valid: password.length >= normalizedPolicy.minLength,
    },
  ];

  if (normalizedPolicy.maxLength) {
    checks.push({
      key: "maxLength",
      label: `Meno di ${normalizedPolicy.maxLength} caratteri`,
      valid: password.length < normalizedPolicy.maxLength,
    });
  }

  if (normalizedPolicy.requireLowercase) {
    checks.push({
      key: "lowercase",
      label: "Una lettera minuscola",
      valid: /[a-z]/.test(password),
    });
  }

  if (normalizedPolicy.requireUppercase) {
    checks.push({
      key: "uppercase",
      label: "Una lettera maiuscola",
      valid: /[A-Z]/.test(password),
    });
  }

  if (normalizedPolicy.requireNumbers) {
    checks.push({
      key: "number",
      label: "Un numero",
      valid: /\d/.test(password),
    });
  }

  if (normalizedPolicy.requireSpecialChar) {
    checks.push({
      key: "specialCharacter",
      label: "Un carattere speciale",
      valid: containsAllowedSpecialCharacter(
        password,
        normalizedPolicy.allowedSpecialCharacters
      ),
    });
  }

  return checks;
}

export function isPasswordPolicySatisfied(password, policy) {
  return getPasswordPolicyChecks(password, policy).every(
    (check) => check.valid
  );
}

export function getPasswordPolicyError(password, policy) {
  const failedChecks = getPasswordPolicyChecks(password, policy).filter(
    (check) => !check.valid
  );

  if (!failedChecks.length) return "";

  return `La password deve contenere: ${failedChecks
    .map((check) => check.label.toLowerCase())
    .join(", ")}.`;
}
